<template>
  <div>
    <BCard>
      <BRow cols="12">
        <BCol>
          <p class="text-size-14">申請額</p>
        </BCol>
        <BCol>
          <p class="text-size-14 text-right">
            ¥ {{ currency(applicationAmount) }}
          </p>
        </BCol>
      </BRow>
      <div class="border-bottom my-2" />
      <BRow>
        <BCol>
          <p class="text-size-14">手数料</p>
        </BCol>
        <BCol>
          <p class="text-size-14 text-right">¥ {{ currency(fee) }}</p>
        </BCol>
      </BRow>
      <div class="border-bottom my-2" />
      <BRow>
        <BCol>
          <p class="text-size-14">振込額</p>
        </BCol>
        <BCol>
          <p class="text-size-14 text-right">
            ¥ {{ currency(transferAmount) }}
          </p>
        </BCol>
      </BRow>
      <div class="border-bottom my-2" />
      <BRow>
        <BCol>
          <AppButton
            v-if="hasMoreThanMinApplicationAmount"
            :block="true"
            class="mt-2"
            variant="success"
            @click="onClickTransfer"
          >
            振込申請
          </AppButton>
          <AppButton
            v-else
            :block="true"
            :disabled="true"
            class="disable-btn mt-3"
            variant="gray-03"
          >
            <p>振込申請</p>
            <p class="sub text-size-10">
              {{ minApplicationAmountErrorMessage }}
            </p>
          </AppButton>
          <p class="text-mild text-size-14 mt-3">
            早期引出手数料は無料です。
          </p>
        </BCol>
      </BRow>
      <BRow v-show="hasRewardTransactionHistories">
        <BCol>
          <p class="text-mild text-size-14 mt-3 mb-3">
            申請額には、以下のお仕事が含まれます。
          </p>
        </BCol>
      </BRow>
      <BListGroup v-show="hasRewardTransactionHistories">
        <div>
          <BListGroupItem
            v-for="rewardTransactionHistory in rewardTransactionHistories"
            :key="rewardTransactionHistory.id"
          >
            <BRow>
              <BCol cols="12">
                <div class="d-block py-3">
                  <div
                    class="d-flex justify-content-between align-items-center"
                  >
                    <p class="text-size-14 text-weight-500">
                      {{
                        toDisplayDateFromCreatedAt(
                          rewardTransactionHistory.createdAt
                        )
                      }}
                    </p>
                    <div class="d-flex align-items-center">
                      <p class="text-blue">
                        + ¥
                        {{
                          currency(
                            rewardTransactionHistoryAmount(
                              rewardTransactionHistory
                            )
                          )
                        }}
                      </p>
                    </div>
                  </div>
                  <div class="mt-1">
                    <p class="text-gray-05 text-size-12">
                      {{
                        getRewardTransactionHistoryDetail(
                          rewardTransactionHistory
                        )
                      }}
                    </p>
                  </div>
                </div>
              </BCol>
            </BRow>
          </BListGroupItem>
        </div>
      </BListGroup>
    </BCard>
    <BModal
      id="transfer-request-modal"
      title="早期引出申請確認"
      hide-footer
      centered
    >
      <div>
        <div class="d-flex justify-content-between mt-3">
          <p class="text-size-16 text-weight-500">総計</p>
          <p class="text-blue text-size-16 text-weight-500">
            ¥ {{ currency(transferAmount) }}
          </p>
        </div>
      </div>
      <BRow>
        <BCol>
          <AppButton
            :block="true"
            class="text-weight-500 mt-3"
            variant="warning"
            @click="onClickCancel"
          >
            キャンセル
          </AppButton>
        </BCol>
        <BCol>
          <AppButton
            :block="true"
            class="text-weight-500 mt-3"
            variant="primary"
            @click="onClickConfirm"
          >
            確定
          </AppButton>
        </BCol>
      </BRow>
    </BModal>
  </div>
</template>

<script lang="ts">
import AppButton from "@/components/atoms/button/AppButton.vue";
import { BalanceHistory, SimpleOrder } from "@/types/balance/BalanceHistory";
import { Component, Vue, Prop } from "vue-facing-decorator";
import { formatNumber } from "@/utils/number";
import { formatFromISOString } from "@/utils/date";
import { applicationService } from "@/app/services/ApplicationService";

const MIN_AMOUNT = 1000;

@Component({
  components: {
    AppButton
  }
})
export default class TransferRequestCard extends Vue {
  @Prop({ type: Array, default: () => [] })
  rewardTransactionHistories!: BalanceHistory[];

  @Prop({ type: Number, default: 0 })
  balance!: number;

  rewardTransactionHistoryAmount(rewardTransactionHistory: BalanceHistory) {
    return (
      rewardTransactionHistory.amountExcludeOption +
      rewardTransactionHistory.optionValue
    );
  }

  getRewardTransactionHistoryDetail(rewardTransactionHistory: BalanceHistory) {
    if (!rewardTransactionHistory.order) {
      return "";
    }
    const s = `${
      rewardTransactionHistory.order.office.name
    } ${this.toDisplayOrderTime(rewardTransactionHistory.order)}`;
    if (rewardTransactionHistory.application) {
      return `${s} ${applicationService.getFormattedWorkTimeRange(
        rewardTransactionHistory.application
      )}`;
    }
    return s;
  }

  toDisplayOrderTime(order: SimpleOrder) {
    return formatFromISOString(order.date, "yy/MM/dd");
  }

  onClickTransfer() {
    this.$bvModal.show("transfer-request-modal");
  }

  onClickConfirm() {
    this.$bvModal.hide("transfer-request-modal");
    this.$loading.show();
    const rewardTransactionHistoryIds = this.rewardTransactionHistories.map(
      trh => trh.id
    );
    this.$emit(
      "requestWithdraw",
      this.transferAmount,
      rewardTransactionHistoryIds
    );
  }

  onClickCancel() {
    this.$bvModal.hide("transfer-request-modal");
  }

  toDisplayDateFromCreatedAt(createdAt: string) {
    return formatFromISOString(createdAt, "yy/MM/dd");
  }

  currency(value: number) {
    return formatNumber(value);
  }

  get hasRewardTransactionHistories(): boolean {
    return this.rewardTransactionHistories.length > 0;
  }

  get applicationAmount() {
    return this.balance;
  }
  get transferAmount(): number {
    const amount = Number(this.applicationAmount);
    if (!amount) return 0;
    return amount - this.fee;
  }

  get hasMoreThanMinApplicationAmount() {
    return this.applicationAmount >= MIN_AMOUNT;
  }

  get minApplicationAmountErrorMessage() {
    return `${MIN_AMOUNT}円以上から申請できます`;
  }

  get fee() {
    return 0;
  }
}
</script>
